import { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, CheckCircle2, Clock, AlertTriangle, Circle, Info, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { ops, isMgmt } from '../lib/ops';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const STATE = {
  done: { label: 'Done', cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', icon: CheckCircle2 },
  overdue: { label: 'Overdue', cls: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500', icon: AlertTriangle },
  upcoming: { label: 'Due soon', cls: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', icon: Clock },
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-600', dot: 'bg-slate-300', icon: Circle },
};
const RECUR_LABEL = { none: 'Manual', quarterly: 'Quarterly', semiannual: 'Every 6 months', annual: 'Annual' };

function ItemsPanel({ storeId, embedded }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { ops.get('/compliance', { store_id: storeId }).then(setData).catch(e => toast.error(e.response?.data?.detail || 'Failed to load')); }, [storeId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-6 text-slate-500 text-sm" data-testid="compliance-loading">Loading…</div>;

  const setDue = async (id, due_date) => { try { await ops.patch(`/compliance/${id}`, { due_date }); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const setRecur = async (id, recurrence) => { try { await ops.patch(`/compliance/${id}`, { recurrence }); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const complete = async (id) => { try { await ops.post(`/compliance/${id}/complete`); toast.success('Marked complete'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const reopen = async (id) => { try { await ops.post(`/compliance/${id}/reopen`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  return (
    <div data-testid="compliance-items-panel">
      {!embedded && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Compliance</p>
            <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{data.store.name}</h1>
          </div>
          <ComplianceRing pct={data.pct} />
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-slate-600">{data.completed}/{data.total} complete</span>
          <div className="flex-1 mx-3 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#F26522]" style={{ width: `${data.pct}%` }} /></div>
          <span className="text-sm font-black text-[#3E2418]">{data.pct}%</span>
        </div>
      )}
      <div className="space-y-2">
        {data.items.map(it => {
          const st = STATE[it.state] || STATE.pending;
          const StIcon = st.icon;
          return (
            <div key={it.id} className="rounded-xl border border-slate-200 p-3 sm:p-4" data-testid={`compliance-item-${it.item_key}`}>
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${st.cls}`}><StIcon className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-[#3E2418]" style={{ fontFamily: 'Chivo, sans-serif' }}>{it.item_label}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${st.cls}`} data-testid={`compliance-state-${it.item_key}`}>{st.label}</span>
                    {it.note && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5"><Info className="w-3 h-3" />{it.note}</span>}
                  </div>
                  {it.status === 'done' && it.completed_by && (
                    <p className="text-xs text-emerald-600 mt-1">Completed by {it.completed_by}{it.completed_at ? ` · ${new Date(it.completed_at).toLocaleDateString()}` : ''}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      Due
                      {data.can_set_due ? (
                        <input type="date" value={it.due_date || ''} onChange={e => setDue(it.id, e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`compliance-due-${it.item_key}`} />
                      ) : (
                        <span className="font-semibold text-slate-700">{it.due_date || 'Not set'}</span>
                      )}
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      Repeats
                      {data.can_set_due ? (
                        <select value={it.recurrence} onChange={e => setRecur(it.id, e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`compliance-recur-${it.item_key}`}>
                          {data.recurrence_options.map(r => <option key={r} value={r}>{RECUR_LABEL[r]}</option>)}
                        </select>
                      ) : <span className="font-semibold text-slate-700">{RECUR_LABEL[it.recurrence]}</span>}
                    </label>
                  </div>
                </div>
                <div className="shrink-0">
                  {data.can_complete && (it.status === 'done'
                    ? <Button onClick={() => reopen(it.id)} variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600 h-8 px-2" data-testid={`compliance-reopen-${it.item_key}`}><RotateCcw className="w-4 h-4" /></Button>
                    : <Button onClick={() => complete(it.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-8 px-3 text-xs font-semibold" data-testid={`compliance-complete-${it.item_key}`}>Mark complete</Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComplianceRing({ pct, size = 60 }) {
  const r = (size - 7) / 2, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  const color = pct === 100 ? '#10B981' : pct >= 50 ? '#F26522' : '#ef4444';
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90"><circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" className="stroke-slate-100" fill="none" /><circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" stroke={color} fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" /></svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-black text-[#3E2418]">{pct}%</span>
    </div>
  );
}

function AdminOverview() {
  const [d, setD] = useState(null);
  const [openStore, setOpenStore] = useState(null);
  useEffect(() => { ops.get('/compliance/overview').then(setD).catch(e => toast.error(e.response?.data?.detail || 'Failed to load')); }, []);
  if (!d) return <div className="py-8 text-slate-500 text-sm" data-testid="compliance-overview-loading">Loading compliance…</div>;
  return (
    <div className="space-y-5" data-testid="compliance-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Restaurants', d.summary.store_count, 'text-[#3E2418]'], ['Avg compliance', `${d.summary.avg_pct}%`, 'text-[#F26522]'], ['Overdue items', d.summary.overdue, d.summary.overdue ? 'text-rose-600' : 'text-emerald-600'], ['Due soon', d.summary.upcoming, d.summary.upcoming ? 'text-amber-600' : 'text-slate-500']].map(([l, v, c]) => (
          <div key={l} className="bg-white border border-[#E5E1DA] rounded-2xl p-4">
            <p className={`text-3xl font-black ${c}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{v}</p>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-1">{l}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {d.stores.map(s => (
          <button key={s.store_id} onClick={() => setOpenStore(s)} className="text-left bg-white border border-[#E5E1DA] rounded-2xl p-4 hover:shadow-md hover:border-[#F26522]/40 transition-all" data-testid={`compliance-store-${s.store_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#3E2418] truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.store_name}</p>
                <p className="text-xs text-slate-400 truncate">{s.operator_name || 'No operator'}{s.province ? ` · ${s.province}` : ''}</p>
              </div>
              <ComplianceRing pct={s.pct} size={52} />
            </div>
            <div className="flex items-center gap-1.5 mt-3">
              {s.items.map(it => <span key={it.item_key} title={`${it.item_label}: ${it.state}`} className={`flex-1 h-1.5 rounded-full ${(STATE[it.state] || STATE.pending).dot}`} />)}
            </div>
            <div className="flex gap-2 mt-2">
              {s.overdue > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-rose-100 text-rose-700">{s.overdue} overdue</span>}
              {s.upcoming > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">{s.upcoming} due soon</span>}
              <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700">{s.completed}/{s.total} done</span>
            </div>
          </button>
        ))}
      </div>

      {openStore && (
        <Dialog open onOpenChange={() => setOpenStore(null)}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="compliance-store-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#3E2418]" style={{ fontFamily: 'Chivo, sans-serif' }}>{openStore.store_name} · Compliance</DialogTitle>
              <DialogDescription className="sr-only">Set due dates, recurrence and mark compliance items complete</DialogDescription>
            </DialogHeader>
            <ItemsPanel storeId={openStore.store_id} embedded />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  return (
    <div className="space-y-5 max-w-5xl" data-testid="compliance-page">
      {mgmt && (
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#F26522]" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Compliance Tracker</p>
            <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>All Restaurants</h1>
          </div>
        </div>
      )}
      {mgmt ? <AdminOverview /> : <ItemsPanel storeId={user?.store_id} />}
    </div>
  );
}
